import React, { useState, useEffect } from 'react'
import { api } from '../../utils/api'
import './affiliates.css'

// Admin-only: per-domain buy-price uplift. Any quote from a matching domain gets
// +bonus% on every item (applied + locked when the assessment page is opened).
function TradeBoost() {
  const [boosts, setBoosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState(null)

  useEffect(() => { loadBoosts() }, [])

  async function loadBoosts() {
    setLoading(true)
    try {
      const res = await api.get('/api/affiliates/trade-boosts')
      setBoosts(res.trade_boosts || [])
    } catch (error) {
      console.error('Failed to load trade boosts:', error)
    } finally {
      setLoading(false)
    }
  }

  async function createBoost(body) {
    await api.post('/api/affiliates/trade-boosts', body)
    setAdding(false)
    await loadBoosts()
  }

  async function patchBoost(id, body) {
    await api.patch(`/api/affiliates/trade-boosts/${id}`, body)
    setEditingId(null)
    await loadBoosts()
  }

  async function deleteBoost(id, name) {
    if (!confirm(`Delete the trade boost for ${name}? New quotes from that domain will no longer be boosted.`)) return
    try {
      await api.delete(`/api/affiliates/trade-boosts/${id}`)
      await loadBoosts()
    } catch (error) {
      console.error('Failed to delete trade boost:', error)
    }
  }

  if (loading) return <div className="affiliate-loading">Loading trade boosts…</div>

  return (
    <div className="affiliate-page">
      <div className="affiliate-head">
        <p className="text-sm text-muted">
          Add a partner who trades in to us themselves. Every item on their quotes gets the bonus added to the buy price.
        </p>
        {!adding && <button className="btn btn-sm btn-primary" onClick={() => setAdding(true)}>Add boosted customer</button>}
      </div>

      {adding && <BoostForm onSave={createBoost} onCancel={() => setAdding(false)} />}

      {boosts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">No boosted customers yet</div>
          <div className="empty-state-description">Add a partner domain (e.g. @soundgas.com) to start boosting their trade-in buy prices.</div>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Domain</th><th>Bonus</th><th>Notes</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {boosts.map(b => (
              <BoostRow
                key={b.trade_boost_id}
                boost={b}
                editing={editingId === b.trade_boost_id}
                onEdit={() => setEditingId(b.trade_boost_id)}
                onCancel={() => setEditingId(null)}
                onSave={(body) => patchBoost(b.trade_boost_id, body)}
                onDelete={() => deleteBoost(b.trade_boost_id, b.name)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function BoostRow({ boost, editing, onEdit, onCancel, onSave, onDelete }) {
  if (editing) {
    return (
      <tr>
        <td colSpan={6}>
          <BoostForm initial={boost} onSave={onSave} onCancel={onCancel} />
        </td>
      </tr>
    )
  }
  return (
    <tr style={boost.active ? undefined : { opacity: 0.5 }}>
      <td>{boost.name}</td>
      <td><code>@{boost.email_domain}</code></td>
      <td><span className="badge badge-success">+{boost.bonus_pct}%</span></td>
      <td className="text-sm text-muted">{boost.notes || '-'}</td>
      <td>{boost.active ? <span className="badge badge-success">Active</span> : <span className="text-sm text-muted">Inactive</span>}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <button className="btn btn-secondary" onClick={onEdit}>Edit</button>{' '}
        <button className="btn btn-secondary" onClick={onDelete}>Delete</button>
      </td>
    </tr>
  )
}

// Shared add/edit form. `domain` is entered with or without a leading @ - the
// backend normalises and rejects consumer mailboxes (gmail.com etc).
function BoostForm({ initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || '')
  const [domain, setDomain] = useState(initial?.email_domain || '')
  const [pct, setPct] = useState(initial?.bonus_pct ?? 5)
  const [notes, setNotes] = useState(initial?.notes || '')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  async function submit() {
    setError('')
    if (!name.trim()) return setError('Name is required')
    if (!domain.trim()) return setError('Domain is required')
    setSaving(true)
    try {
      await onSave({ name: name.trim(), email_domain: domain.trim(), bonus_pct: Number(pct), notes: notes.trim() || null })
    } catch (e) {
      setError(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="affiliate-form">
        <div className="filter-group">
          <label className="filter-label">Name</label>
          <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Tony at Soundgas" maxLength={200} />
        </div>
        <div className="filter-group">
          <label className="filter-label">Domain</label>
          <input className="form-input" value={domain} onChange={e => setDomain(e.target.value)} placeholder="@soundgas.com" maxLength={200} />
        </div>
        <div className="filter-group narrow">
          <label className="filter-label">Bonus %</label>
          <input className="form-input" type="number" min="0" max="100" step="0.5" value={pct} onChange={e => setPct(e.target.value)} />
        </div>
      </div>
      <div className="filter-group filter-group-wide" style={{ marginTop: 12 }}>
        <label className="filter-label">Notes</label>
        <textarea className="form-input" value={notes} onChange={e => setNotes(e.target.value)} rows={2} maxLength={2000} placeholder="Anything about this relationship" />
      </div>
      {error && <div className="text-sm" style={{ color: 'var(--danger, #c0392b)', marginTop: 8 }}>{error}</div>}
      <div className="filter-actions" style={{ marginTop: 12 }}>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>{' '}
        <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  )
}

export default TradeBoost
